<template>
  <div class="settings">
    <h2>설정</h2>

    <section class="card">
      <h3>내 계정</h3>
      <p class="muted">user_id: <code>{{ user.user_id }}</code></p>
      <p class="muted" v-if="user.email">email: {{ user.email }}</p>
    </section>

    <section class="card">
      <h3>개인 액세스 토큰 (Siri · 단축어 · MCP)</h3>
      <p class="muted">
        아이폰 Siri/단축어나 AI Agent 가 내 일정을 다루려면 이 토큰을 사용합니다.
        요청 헤더에 <code>Authorization: Bearer &lt;토큰&gt;</code> 로 넣으세요.
      </p>

      <div class="issue">
        <input v-model="newName" placeholder="토큰 이름 (예: 아이폰 Siri)" @keyup.enter="create" />
        <button class="primary" @click="create" :disabled="creating">발급</button>
      </div>

      <div v-if="justCreated" class="reveal">
        <strong>새 토큰 (지금만 표시됩니다):</strong>
        <div class="token-box">
          <code>{{ justCreated }}</code>
          <button class="ghost" @click="copy(justCreated)">복사</button>
        </div>
      </div>

      <table v-if="tokens.length" class="tokens">
        <thead><tr><th>이름</th><th>토큰</th><th>마지막 사용</th><th></th></tr></thead>
        <tbody>
          <tr v-for="t in tokens" :key="t.id">
            <td>{{ t.name }}</td>
            <td><code>{{ t.token_prefix }}…</code></td>
            <td class="muted">{{ t.last_used_at || '—' }}</td>
            <td><button class="danger" @click="remove(t.id)">폐기</button></td>
          </tr>
        </tbody>
      </table>
      <p v-else class="muted">발급된 토큰이 없습니다.</p>
    </section>

    <section class="card">
      <h3>태그 설정</h3>
      <p class="muted">
        일정에 붙일 태그를 만들어보세요. 색상뿐 아니라 이모지도 함께 표시되어
        한눈에 구분하기 쉬워집니다.
      </p>

      <ul v-if="tags.length" class="tag-list">
        <li v-for="t in tags" :key="t.id" class="tag-row">
          <template v-if="editingTagId === t.id">
            <span class="tag-preview" :style="{ background: editForm.color }">{{ editForm.emoji }}</span>
            <input v-model="editForm.name" class="tag-name-input" placeholder="태그 이름" />
            <div class="mini-swatches">
              <button
                v-for="c in colors" :key="c.value" type="button" class="mini-swatch"
                :class="{ sel: editForm.color === c.value }" :style="{ '--sw': c.value }" :title="c.name"
                @click="editForm.color = c.value"
              ></button>
            </div>
            <input v-model="editForm.emoji" class="emoji-input" maxlength="4" placeholder="직접 입력" />
            <div class="row-actions">
              <button type="button" class="ghost" @click="cancelEdit">취소</button>
              <button type="button" class="primary" @click="saveEdit(t.id)">저장</button>
            </div>
          </template>
          <template v-else>
            <span class="tag-preview" :style="{ background: t.color }">{{ t.emoji }}</span>
            <span class="tag-name">{{ t.name }}</span>
            <div class="row-actions">
              <button type="button" class="ghost" @click="startEdit(t)">수정</button>
              <button type="button" class="danger" @click="removeTag(t.id)">삭제</button>
            </div>
          </template>
        </li>
      </ul>
      <p v-else class="muted">아직 만든 태그가 없습니다.</p>

      <div class="tag-new">
        <span class="tag-preview" :style="{ background: newTag.color }">{{ newTag.emoji }}</span>
        <input v-model="newTag.name" class="tag-name-input" placeholder="태그 이름 (예: 운동)" @keyup.enter="createTag" />
        <div class="mini-swatches">
          <button
            v-for="c in colors" :key="c.value" type="button" class="mini-swatch"
            :class="{ sel: newTag.color === c.value }" :style="{ '--sw': c.value }" :title="c.name"
            @click="newTag.color = c.value"
          ></button>
        </div>
        <input v-model="newTag.emoji" class="emoji-input" maxlength="4" placeholder="직접 입력" title="키보드 이모지 입력기로 원하는 이모지를 직접 입력할 수 있어요" />
        <p class="emoji-hint">위 칸을 탭해 키보드의 이모지 입력기로 직접 입력하거나, 아래에서 골라보세요.</p>
        <div class="emoji-quick">
          <button
            v-for="e in quickEmojis" :key="e" type="button" class="emoji-btn"
            :class="{ sel: newTag.emoji === e }" @click="newTag.emoji = e"
          >{{ e }}</button>
        </div>
        <button type="button" class="primary" @click="createTag" :disabled="creatingTag">태그 추가</button>
      </div>
    </section>

    <section class="card">
      <div class="log-head">
        <h3><span class="dot" :class="{ off: !rtConnected }"></span> 활동 로그</h3>
        <span class="rt-state">{{ rtConnected ? '실시간 연결됨' : '연결 대기중…' }}</span>
        <button v-if="logs.length" class="ghost" @click="clearLogs">모두 지우기</button>
      </div>
      <p class="muted">
        일정 추가·수정·삭제와 Siri/AI 요청 기록이 실시간으로 쌓입니다.
      </p>

      <ul v-if="logs.length" class="activity-list">
        <li v-for="l in visibleLogs" :key="l.id" :class="l.action">
          <span class="a-time">{{ l.time }}</span>
          <span class="a-badge">{{ l.label }}</span>
          <span class="a-title">{{ l.title }}</span>
          <span class="a-when" v-if="l.when">{{ l.when }}</span>
          <span class="a-detail" v-if="l.detail">{{ l.detail }}</span>
        </li>
      </ul>
      <button v-if="logs.length > 5" class="log-more" @click="logExpanded = !logExpanded">
        {{ logExpanded ? '접기' : `더보기 (${logs.length - 5})` }}
      </button>
      <p v-if="!logs.length" class="muted activity-empty">
        아직 활동이 없어요. 일정을 추가·수정·삭제하면 여기 실시간으로 표시됩니다.
      </p>
    </section>

    <section class="card">
      <h3>MCP / API 엔드포인트</h3>
      <ul class="endpoints">
        <li><b>MCP</b>: <code>POST {{ origin }}/mcp</code> (JSON-RPC 2.0)</li>
        <li><b>REST</b>: <code>{{ origin }}/api/events</code></li>
      </ul>
      <p class="muted">사용 예시는 README 의 “Siri/단축어” 섹션을 참고하세요.</p>
    </section>
  </div>
</template>

<script>
import api from '../api';
import { connectRealtime } from '../lib/realtime';
import { EVENT_COLORS } from '../lib/colors';
import { TAG_EMOJIS } from '../lib/emojis';

export default {
  name: 'SettingsView',
  props: { user: { type: Object, required: true } },
  data() {
    return {
      tokens: [], newName: '', creating: false, justCreated: '', origin: window.location.origin,
      tags: [],
      colors: EVENT_COLORS,
      quickEmojis: TAG_EMOJIS,
      newTag: { name: '', color: EVENT_COLORS[0].value, emoji: '' },
      creatingTag: false,
      editingTagId: null,
      editForm: { name: '', color: '', emoji: '' },
      logs: [],           // 활동 로그 (최신순, DB에서 로드 + 실시간 추가)
      logExpanded: false, // 더보기 여부 (false=5개, true=전체)
      rtConnected: false, // WebSocket 연결 상태
    };
  },
  computed: {
    visibleLogs() {
      // 기본 5개, 더보기 시 최신 30개
      return this.logExpanded ? this.logs.slice(0, 30) : this.logs.slice(0, 5);
    },
  },
  async created() {
    await this.reload();
    await this.reloadTags();
    await this.loadLogs(); // DB에 저장된 활동 로그 복원

    // 실시간: 서버가 보내는 변경/요청 알림을 로그에 바로 추가
    this._rt = connectRealtime(
      (msg) => {
        if (!msg) return;
        if (msg.type === 'event:changed' || msg.type === 'nlp:log') this.pushLog(msg);
      },
      (connected) => { this.rtConnected = connected; }
    );
  },
  unmounted() {
    if (this._rt) this._rt.close();
  },
  methods: {
    // 실시간 활동 로그에 한 줄 추가 (최신이 위로, 최대 30개 유지)
    pushLog(msg) {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      const ss = String(now.getSeconds()).padStart(2, '0');
      this._logSeq = (this._logSeq || 0) + 1;
      const base = { id: this._logSeq, time: `${hh}:${mm}:${ss}` };

      let entry;
      if (msg.type === 'nlp:log') {
        // 이해못함/조회/오류 — 사용자가 한 말(title) + 서버 응답/이유(detail)
        const kinds = {
          clarification: { action: 'clarify', label: '이해못함' },
          error: { action: 'error', label: '오류' },
          query: { action: 'query', label: '조회' },
        };
        const k = kinds[msg.kind] || { action: 'query', label: '요청' };
        entry = { ...base, action: k.action, label: k.label, title: msg.text || '', detail: msg.message || '', when: '' };
      } else {
        // event:changed — 추가/수정/삭제
        const labels = { create: '추가', update: '수정', delete: '삭제' };
        const ev = msg.event || {};
        entry = {
          ...base,
          action: msg.action,
          label: labels[msg.action] || msg.action,
          title: ev.title || (ev.id ? `#${ev.id}` : '일정'),
          when: ev.start_at ? String(ev.start_at).slice(5, 16) : '',
          detail: '',
        };
      }
      this.logs.unshift(entry);
      if (this.logs.length > 30) this.logs.length = 30;
    },
    // DB에서 저장된 로그 복원 (새로고침 후에도 유지)
    async loadLogs() {
      try {
        const res = await api.listActivity();
        this.logs = (res.logs || []).map((x) => {
          this._logSeq = (this._logSeq || 0) + 1;
          return {
            id: this._logSeq,
            time: String(x.created_at || '').slice(11, 19),
            action: x.action,
            label: x.label,
            title: x.title || '',
            detail: x.detail || '',
            when: x.when || '',
          };
        });
      } catch (e) { /* 로그 로드 실패는 무시 */ }
    },
    async clearLogs() {
      if (!confirm('활동 로그를 모두 지울까요?')) return;
      try { await api.clearActivity(); } catch (e) { /* 무시 */ }
      this.logs = [];
      this.logExpanded = false;
    },
    async reload() {
      try { this.tokens = (await api.listTokens()).tokens; } catch (e) { /* noop */ }
    },
    async reloadTags() {
      try { this.tags = (await api.listTags()).tags; } catch (e) { /* noop */ }
    },
    async createTag() {
      const name = this.newTag.name.trim();
      if (!name) return;
      if (this.creatingTag) return;
      this.creatingTag = true;
      try {
        await api.createTag({ name, color: this.newTag.color, emoji: this.newTag.emoji || null });
        this.newTag = { name: '', color: EVENT_COLORS[0].value, emoji: '' };
        await this.reloadTags();
      } catch (e) {
        alert('태그 추가 실패: ' + e.message);
      } finally {
        this.creatingTag = false;
      }
    },
    startEdit(t) {
      this.editingTagId = t.id;
      this.editForm = { name: t.name, color: t.color, emoji: t.emoji || '' };
    },
    cancelEdit() { this.editingTagId = null; },
    async saveEdit(id) {
      const name = this.editForm.name.trim();
      if (!name) return;
      try {
        await api.updateTag(id, { name, color: this.editForm.color, emoji: this.editForm.emoji || null });
        this.editingTagId = null;
        await this.reloadTags();
      } catch (e) {
        alert('태그 수정 실패: ' + e.message);
      }
    },
    async removeTag(id) {
      if (!confirm('이 태그를 삭제할까요? 이 태그가 붙은 일정은 태그 없음 상태가 됩니다.')) return;
      try {
        await api.deleteTag(id);
        await this.reloadTags();
      } catch (e) {
        alert('태그 삭제 실패: ' + e.message);
      }
    },
    async create() {
      if (this.creating) return;
      this.creating = true;
      try {
        const res = await api.createToken(this.newName || '토큰');
        this.justCreated = res.token;
        this.newName = '';
        await this.reload();
      } catch (e) {
        alert('발급 실패: ' + e.message);
      } finally {
        this.creating = false;
      }
    },
    async remove(id) {
      if (!confirm('이 토큰을 폐기할까요? 해당 토큰을 쓰던 단축어는 동작하지 않습니다.')) return;
      await api.deleteToken(id).catch(() => {});
      await this.reload();
    },
    copy(text) {
      navigator.clipboard?.writeText(text).then(
        () => alert('복사되었습니다.'),
        () => {}
      );
    },
  },
};
</script>

<style scoped>
.settings { display: flex; flex-direction: column; gap: 18px; }
.card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 18px; }
.card h3 { margin: 0 0 10px; }
.muted { color: var(--muted); font-size: 14px; }
code { background: var(--bg); padding: 2px 6px; border-radius: 6px; font-size: 13px; }
.issue { display: flex; gap: 10px; margin: 12px 0; }
.issue input { flex: 1; }
.reveal { background: var(--accent-weak); border-radius: 10px; padding: 12px; margin-bottom: 12px; }
.token-box { display: flex; gap: 10px; align-items: center; margin-top: 6px; }
.token-box code { flex: 1; overflow-wrap: anywhere; }
.tokens { width: 100%; border-collapse: collapse; margin-top: 10px; }
.tokens th, .tokens td { text-align: left; padding: 8px; border-bottom: 1px solid var(--border); font-size: 14px; }
.endpoints { margin: 0; padding-left: 18px; line-height: 2; }

/* 활동 로그 */
.log-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.log-head h3 { margin: 0; display: flex; align-items: center; gap: 7px; }
.log-head .rt-state { font-size: 11px; color: var(--muted); }
.log-head .ghost { margin-left: auto; }
.dot {
  display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #22c55e;
  box-shadow: 0 0 0 0 rgba(34,197,94,0.5); animation: pulse 1.6s infinite;
}
.dot.off { background: #9ca3af; animation: none; box-shadow: none; }
@keyframes pulse { 70% { box-shadow: 0 0 0 6px rgba(34,197,94,0); } 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); } }
.activity-empty { margin: 14px 0 0; text-align: center; }
.activity-list {
  list-style: none; margin: 12px 0 0; padding: 0;
  border: 1px solid var(--border); border-radius: 12px; overflow: hidden;
  background: var(--surface-2);
}
.activity-list li {
  display: flex; align-items: center; flex-wrap: wrap; gap: 4px 8px;
  padding: 7px 12px; font-size: 13px; border-bottom: 1px solid var(--border);
}
.activity-list li:last-child { border-bottom: none; }
.a-time { color: var(--muted); font-size: 12px; font-variant-numeric: tabular-nums; }
.a-badge {
  font-size: 11px; font-weight: 700; padding: 1px 7px; border-radius: 999px;
  background: var(--accent-weak); color: var(--accent);
}
.activity-list li.create .a-badge { background: #dcfce7; color: #16a34a; }
.activity-list li.delete .a-badge { background: #fee2e2; color: #dc2626; }
.activity-list li.update .a-badge { background: #dbeafe; color: #2563eb; }
.activity-list li.clarify .a-badge { background: #fef3c7; color: #d97706; }
.activity-list li.error .a-badge { background: #fee2e2; color: #dc2626; }
.activity-list li.query .a-badge { background: var(--accent-weak); color: var(--accent); }
.a-detail { width: 100%; margin-left: 52px; font-size: 12px; color: var(--muted); }
.a-title { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.a-when { margin-left: auto; color: var(--muted); font-size: 12px; font-variant-numeric: tabular-nums; }
.log-more {
  width: 100%; border: none; background: none; color: var(--accent);
  font-size: 13px; font-weight: 600; padding: 10px; cursor: pointer;
}
.log-more:hover { background: var(--accent-weak); border-radius: 8px; }

/* 태그 설정 */
.tag-list { list-style: none; margin: 12px 0 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.tag-row, .tag-new {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 10px 12px; border: 1px solid var(--border); border-radius: 12px; background: var(--surface-2);
}
.tag-new { margin-top: 14px; }
.tag-preview {
  flex: 0 0 auto; width: 28px; height: 28px; border-radius: 50%;
  display: grid; place-items: center; font-size: 15px; line-height: 1; color: #fff;
}
.tag-name { flex: 1; min-width: 60px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tag-name-input { flex: 1; min-width: 100px; }
.row-actions { display: flex; gap: 8px; margin-left: auto; }
.mini-swatches { display: flex; gap: 6px; flex-wrap: wrap; }
.mini-swatch {
  width: 22px; height: 22px; padding: 0; border-radius: 50%; flex: 0 0 auto;
  border: 2px solid transparent; background: var(--sw); cursor: pointer;
}
.mini-swatch.sel { box-shadow: 0 0 0 2px var(--surface), 0 0 0 4px var(--sw); }
.emoji-input { width: 64px; text-align: center; font-size: 16px; flex: 0 0 auto; }
.emoji-hint { flex-basis: 100%; margin: -4px 0 0; font-size: 11px; color: var(--muted); }
.emoji-quick { display: flex; gap: 4px; flex-wrap: wrap; max-width: 220px; }
.emoji-btn {
  width: 30px; height: 30px; padding: 0; font-size: 16px; line-height: 1;
  border-radius: 8px; background: var(--surface); border: 1px solid var(--border);
}
.emoji-btn.sel { border-color: var(--accent); background: var(--accent-weak); }
.emoji-btn:hover { background: var(--accent-weak); }

@media (max-width: 640px) {
  .tag-row, .tag-new { flex-direction: column; align-items: stretch; }
  .tag-row .tag-preview, .tag-new .tag-preview { align-self: flex-start; }
  .row-actions { margin-left: 0; justify-content: flex-end; }
  .emoji-quick { max-width: none; }
  .tag-new > button.primary { width: 100%; }
}
</style>
